import { FC } from 'react';
import styled from '@emotion/styled';
import {
  Card,
  CardContent,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Tooltip,
  Divider,
} from '@material-ui/core';
import { Assignment as AssignmentIcon } from '@material-ui/icons';
import { LockOpen as LockOpenIcon } from '@material-ui/icons';
import HelpIcon from '@material-ui/icons/Help';

import { PaperTitle, PaperTitleText, PaperContent, StyledPaper } from '../../primitive/Paper';

const EmptyChart: FC = () => {
  return (
    <Container>
      <ChartAria>
        <EmptyIconWrapper>
          <AssignmentIcon style={{ fontSize: 80, color: '#bdbdbd' }} />
        </EmptyIconWrapper>
        <EmptyTitle>データがありません</EmptyTitle>
        <EmptyDescription>
          選択された期間のデータが見つかりません。期間を変更するか、しばらくお待ちください。
        </EmptyDescription>
      </ChartAria>
      <MetricsContainer>
        {['DOM Content Loaded (DCL)', 'Largest Contentful Paint (LCP)', 'Cumulative Layout Shift (CLS)'].map(
          (label, index) => (
            <MetricCard key={index}>
              <CardContent>
                <MetricLabel variant="body2">{label}</MetricLabel>
                <MetricValues>
                  <MetricValue variant="h6">--</MetricValue>
                  <ArrowIcon>→</ArrowIcon>
                  <MetricValue variant="h6">--</MetricValue>
                </MetricValues>
                <ImprovementChipEmpty label="データなし" />
              </CardContent>
            </MetricCard>
          ),
        )}
      </MetricsContainer>
    </Container>
  );
};

const EmptyTable: FC = () => {
  return (
    <Container>
      <PaperTitle>
        <PaperTitleText>ページ別A/Bテスト結果</PaperTitleText>
      </PaperTitle>
      <PaperContent>
        <StyledTable>
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}>
                <UrlWithPv>
                  ページURL
                  <Tooltip title="速度改善ツールが起動して正常計測されたページビュー数です。対象OS・ブラウザでのアクセス時にのみカウントされ、異常値は除外されます。">
                    <PvText>
                      (有効PV
                      <HelpIcon style={{ fontSize: 16, marginBottom: -3, paddingLeft: 3 }} />)
                    </PvText>
                  </Tooltip>
                </UrlWithPv>
              </TableCell>
              <TableCell align="center" colSpan={2}>
                未設定グループ (10%)
              </TableCell>
              <TableCell rowSpan={2} align="center" style={{ width: '2%' }}></TableCell>
              <TableCell align="center" colSpan={2}>
                設定グループ (90%)
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="center" style={{ width: '10%' }}>
                DCL
              </TableCell>
              <TableCell align="center" style={{ width: '10%' }}>
                LCP
              </TableCell>
              <TableCell align="center" style={{ width: '10%' }}>
                DCL
              </TableCell>
              <TableCell align="center" style={{ width: '10%' }}>
                LCP
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan={6}>
                <TableArea>
                  <EmptyIconWrapper>
                    <LockOpenIcon style={{ fontSize: 60, color: '#bdbdbd' }} />
                  </EmptyIconWrapper>
                  <EmptyTitle>ページデータがありません</EmptyTitle>
                  <EmptyDescription>
                    まだA/Bテストの結果がありません。データが収集されるまでお待ちください。
                  </EmptyDescription>
                </TableArea>
              </TableCell>
            </TableRow>
          </TableBody>
        </StyledTable>
      </PaperContent>
    </Container>
  );
};

const ReportEmpty: FC = () => {
  return (
    <Container>
      <StyledPaper style={{ marginTop: 0 }}>
        <PaperContent>
          <PaperTitle>
            <PaperTitleText>
              速度改善レポート
              <PaperTitleNote>(毎日午前3時頃に更新)</PaperTitleNote>
            </PaperTitleText>
          </PaperTitle>
          <Divider style={{ marginTop: 12, marginBottom: 16 }} />
          <>
            <EmptyChart />
            <Divider style={{ marginTop: 24, marginBottom: 24 }} />
            <EmptyTable />
          </>
        </PaperContent>
      </StyledPaper>
    </Container>
  );
};

const Container = styled('div')({
  width: '100%',
});

const ChartAria = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '420px',
  marginTop: '8px',
  backgroundColor: '#f8f9fa',
  border: '1px solid #e6e7e8',
  padding: '0 24px 24px 24px',
  borderRadius: '4px',
});

const TableArea = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '80px 32px',
  minHeight: '200px',
});

const EmptyIconWrapper = styled('div')({
  marginBottom: '24px',
});

const EmptyTitle = styled(Typography)({
  fontSize: '18px',
  fontWeight: 700,
  color: '#424242',
  marginBottom: '12px',
});

const EmptyDescription = styled(Typography)({
  fontSize: '14px',
  color: '#757575',
  textAlign: 'center',
  lineHeight: '1.6',
});

const MetricsContainer = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '16px',
  marginTop: '24px',
  padding: '24px',
  backgroundColor: '#f8f9fa',
  border: '1px solid #e6e7e8',
  borderRadius: '4px',
});

const MetricCard = styled(Card)({
  textAlign: 'center',
  opacity: 0.7,
});

const MetricLabel = styled(Typography)({
  marginBottom: '8px',
  fontWeight: 500,
});

const MetricValues = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  marginBottom: '8px',
});

const MetricValue = styled(Typography)({
  fontWeight: 600,
});

const ImprovementChipEmpty = styled(Chip)({
  fontWeight: 600,
  size: 'small',
  backgroundColor: '#f5f5f5',
  color: '#bdbdbd',
});

const ArrowIcon = styled('span')({
  color: '#666',
  fontSize: '18px',
});

const StyledTable = styled(Table)({
  marginTop: '20px',
  border: '1px solid #e6e7e8',
  borderRadius: '4px',
  borderCollapse: 'separate',
});

const UrlWithPv = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const PvText = styled('div')({
  fontSize: '12px',
  color: '#666',
  whiteSpace: 'nowrap',
});

export const PaperTitleNote = styled('span')(`
  font-size: 15px;
  padding-left: 8px;
`);

export default ReportEmpty;
